import { MutableRefObject, useCallback, useEffect, useRef } from 'react';

import { AccessibilityRole } from 'react-native';

import { isWeb } from 'utils/environment';

const FOCUSABLE_MODAL_ELEMENT_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  '[tabindex]:not([tabindex="-1"])',
].join(',');
const MODAL_BACKDROP_DATA_ATTRIBUTE = 'data-modal-focus-trap-backdrop';
// aria-modal="true" alone (regardless of role) reliably marks a nested modal boundary: RN Web's
// own Modal sets it on its role="dialog" content (ModalContent.js), but NativeBase's AlertDialog
// content sets accessibilityRole="alert" instead of "dialog" (AlertDialogContent.tsx) while still
// carrying aria-modal="true" — matching on aria-modal alone covers both without special-casing
// every accessibilityRole a nested modal might use.
const NESTED_MODAL_SELECTOR = '[aria-modal="true"]';

const getActiveHtmlElement = () =>
  document.activeElement instanceof HTMLElement ? document.activeElement : null;

const isOrContainsNestedModal = (element: HTMLElement) =>
  element.matches(NESTED_MODAL_SELECTOR) || !!element.querySelector(NESTED_MODAL_SELECTOR);

// Timing-independent fallback for handleFocusIn/handleKeyDown: rather than only trusting
// allowedNestedModalContainers (populated asynchronously by hideElementsOutsideModal's
// MutationObserver — see reevaluateHiddenElementsForNestedModals), this checks the live DOM
// directly at the moment focus moves. A nested modal's own focus-trapping (e.g. NativeBase's
// FocusScope) can autofocus into it before that MutationObserver's microtask has run, which on
// some browsers loses the race against the very first focus event — this check has no such race,
// since it doesn't depend on anything having run beforehand. Walks up from target to find its
// topmost ancestor under document.body (the portal wrapper, covering ModalFocusTrap's focus
// bracket siblings too, not just descendants of the aria-modal node), then checks whether that
// wrapper is a nested modal and isn't (part of) this trap's own modalElement.
//
// isPreExistingBackgroundElement guards against exempting a modal that already existed (and was
// deliberately hidden as background content) before this trap activated — that decision is made
// synchronously by hideElementsOutsideModal's initial snapshot pass, so it's just as safe to
// trust here as the live DOM shape itself; only a modal that opened after activation should ever
// bypass this trap's focus containment.
const isInsideLiveNestedModal = (
  target: HTMLElement,
  modalElement: HTMLElement,
  isPreExistingBackgroundElement: (element: HTMLElement) => boolean
) => {
  if (modalElement.contains(target)) {
    return false;
  }

  let topmostAncestor: HTMLElement = target;

  while (topmostAncestor.parentElement && topmostAncestor.parentElement !== document.body) {
    topmostAncestor = topmostAncestor.parentElement;
  }

  // topmostAncestor containing modalElement (rather than being a separate nested modal) means
  // target is simply a sibling of modalElement under the same non-body ancestor — e.g. a plain
  // page element that happens to share a wrapper with this trap's own modal — not a nested modal.
  if (topmostAncestor.contains(modalElement)) {
    return false;
  }

  if (isPreExistingBackgroundElement(topmostAncestor)) {
    return false;
  }

  return isOrContainsNestedModal(topmostAncestor);
};

const getModalFocusableElements = (modalElement: HTMLElement) =>
  Array.from(modalElement.querySelectorAll<HTMLElement>(FOCUSABLE_MODAL_ELEMENT_SELECTOR)).filter(
    element =>
      !element.hasAttribute('disabled') &&
      element.tabIndex !== -1 &&
      !element.closest('[aria-hidden="true"]')
  );

const focusFirstModalElement = (modalElement: HTMLElement) => {
  const [firstFocusableElement] = getModalFocusableElements(modalElement);

  (firstFocusableElement ?? modalElement).focus();
};

// Hides (and un-hides on cleanup) every DOM sibling outside the modal's ancestor chain, up to
// document.body, so screen readers and Tab navigation can't reach content behind the modal. A
// MutationObserver keeps hiding siblings mounted after the initial snapshot (e.g. a toast portal
// appended while the modal is open) so they can't steal focus away from the trapped modal either.
const hideElementsOutsideModal = (modalElement: HTMLElement) => {
  const restoreElementCallbacks: Array<() => void> = [];
  const mutationObservers: MutationObserver[] = [];
  let currentElement: HTMLElement | null = modalElement;

  // Elements this trap has hidden, keyed so a later change can find and undo the hide
  // retroactively (see reevaluateHiddenElementsForNestedModals below). allowNestedModalExemption
  // is recorded per-entry so that re-check only applies to elements inserted after this trap
  // activated — pre-existing/background content stays hidden even if it changes later, since it
  // isn't something opened from within this modal.
  const hiddenElements = new Map<
    HTMLElement,
    { restore: () => void; allowNestedModalExemption: boolean }
  >();

  // Containers exempted from hiding because they're a nested modal that opened after this trap
  // activated. Exposed via isInsideAllowedNestedModal so handleFocusIn/handleKeyDown can tell
  // "focus moved into a legitimate nested modal" from "focus escaped" by containment, not just a
  // role match — covering siblings of the dialog node too (react-native-web's ModalFocusTrap
  // focus brackets sit next to, not inside, the role="dialog" element).
  const allowedNestedModalContainers = new Set<HTMLElement>();

  const unhideElement = (element: HTMLElement) => {
    hiddenElements.get(element)?.restore();
    hiddenElements.delete(element);
  };

  const hideElement = (
    element: Element,
    modalAncestorElement: HTMLElement,
    allowNestedModalExemption: boolean
  ) => {
    if (
      !(element instanceof HTMLElement) ||
      element === modalAncestorElement ||
      element.hasAttribute(MODAL_BACKDROP_DATA_ATTRIBUTE) ||
      hiddenElements.has(element)
    ) {
      return;
    }

    if (allowNestedModalExemption && isOrContainsNestedModal(element)) {
      // Any React Native <Modal> portals straight to document.body on web, landing as a
      // sibling of the app root rather than a DOM descendant of this modal. Hiding it here
      // would mark it `inert`, which disables pointer events too — so a nested modal (e.g. the
      // language selector opened from within this trapped drawer) would render on top but
      // silently swallow every click.
      allowedNestedModalContainers.add(element);
      return;
    }

    const previousAriaHidden = element.getAttribute('aria-hidden');
    const hadInertAttribute = element.hasAttribute('inert');

    element.setAttribute('aria-hidden', 'true');
    element.setAttribute('inert', '');

    const restore = () => {
      if (previousAriaHidden === null) {
        element.removeAttribute('aria-hidden');
      } else {
        element.setAttribute('aria-hidden', previousAriaHidden);
      }

      if (!hadInertAttribute) {
        element.removeAttribute('inert');
      }
    };

    hiddenElements.set(element, { restore, allowNestedModalExemption });
    restoreElementCallbacks.push(restore);
  };

  // A React Native Modal's portal wrapper can be appended to document.body empty and only gain
  // role="dialog" on its content a render pass later (an attributes mutation), or a dialog
  // subtree can mount fully-formed as a descendant insertion under an already-hidden wrapper
  // (a childList mutation the observer's direct-child check above doesn't see). Either way, the
  // childList observer's very first pass can hide the wrapper before it's recognizable as a
  // nested modal, so this re-evaluates every previously-hidden element on both kinds of change,
  // un-hiding it if it (or a descendant) has since become a nested modal — but only elements
  // that were eligible for the nested-modal exemption in the first place (i.e. inserted after
  // this trap activated), so pre-existing background content stays hidden.
  const reevaluateHiddenElementsForNestedModals = () => {
    Array.from(hiddenElements.entries()).forEach(([element, { allowNestedModalExemption }]) => {
      if (!document.contains(element)) {
        hiddenElements.delete(element);
        return;
      }

      if (allowNestedModalExemption && isOrContainsNestedModal(element)) {
        allowedNestedModalContainers.add(element);
        unhideElement(element);
      }
    });
  };

  while (currentElement?.parentElement) {
    const parentElement: HTMLElement = currentElement.parentElement;
    const modalAncestorElement: HTMLElement = currentElement;

    // Elements already on screen when this trap activates are pre-existing/background content
    // (not something opened from within this now-active modal), so hide them unconditionally —
    // no nested-modal exemption. Only nodes inserted afterward (below) get the exemption.
    Array.from(parentElement.children).forEach(element =>
      hideElement(element, modalAncestorElement, false)
    );

    const mutationObserver = new MutationObserver(mutations => {
      // See reevaluateHiddenElementsForNestedModals above for why both attribute changes and
      // descendant insertions need to trigger a re-check.
      let shouldReevaluateHiddenElements = false;

      mutations.forEach(mutation => {
        if (mutation.type === 'attributes') {
          shouldReevaluateHiddenElements = true;
          return;
        }

        mutation.addedNodes.forEach(node => {
          if (!(node instanceof HTMLElement)) {
            return;
          }

          if (node.parentElement === parentElement) {
            hideElement(node, modalAncestorElement, true);
          } else {
            shouldReevaluateHiddenElements = true;
          }
        });
      });

      if (shouldReevaluateHiddenElements) {
        reevaluateHiddenElementsForNestedModals();
      }
    });

    mutationObserver.observe(parentElement, {
      childList: true,
      attributes: true,
      attributeFilter: ['role', 'aria-modal'],
      subtree: true,
    });
    mutationObservers.push(mutationObserver);

    if (parentElement === document.body) {
      break;
    }

    currentElement = parentElement;
  }

  const isInsideAllowedNestedModal = (target: HTMLElement) =>
    Array.from(allowedNestedModalContainers).some(container => container.contains(target));

  // Elements hidden with allowNestedModalExemption: false were already on screen when this trap
  // activated — deliberately treated as background content regardless of what they contain (see
  // hideElement above). isInsideLiveNestedModal must never override that, or a pre-existing
  // hidden dialog could still pull focus out of this trap.
  const isPreExistingBackgroundElement = (element: HTMLElement) =>
    hiddenElements.get(element)?.allowNestedModalExemption === false;

  const cleanup = () => {
    mutationObservers.forEach(observer => observer.disconnect());
    restoreElementCallbacks.reverse().forEach(restoreElement => restoreElement());
    allowedNestedModalContainers.clear();
  };

  return { cleanup, isInsideAllowedNestedModal, isPreExistingBackgroundElement };
};

interface UseModalFocusTrapOptions {
  /** Whether the modal is currently open and should trap focus (web only; a no-op elsewhere). */
  active: boolean;
  /** Accessible label announced for the modal region. */
  label: string;
  /**
   * Shared with the caller's own close handling: set to `true` while a close animation is
   * in flight so Tab-loop and focus-steal handling don't fight an element that's mid-teardown.
   * Reset to `false` by this hook whenever the trap (re)activates.
   */
  isClosingRef: MutableRefObject<boolean>;
}

interface GetModalA11yPropsOptions {
  /** Accessibility role to use on native platforms, where 'dialog' isn't a valid role. */
  nativeRole: AccessibilityRole;
}

export const useModalFocusTrap = ({ active, label, isClosingRef }: UseModalFocusTrapOptions) => {
  const modalElementRef = useRef<HTMLElement | null>(null);
  const previouslyFocusedElementRef = useRef<HTMLElement | null>(null);
  const restoreOutsideElementsRef = useRef<(() => void) | null>(null);
  const isInsideAllowedNestedModalRef = useRef<(target: HTMLElement) => boolean>(() => false);
  const isPreExistingBackgroundElementRef = useRef<(element: HTMLElement) => boolean>(() => false);

  const modalRef = useCallback((element: unknown) => {
    modalElementRef.current = element as HTMLElement | null;
  }, []);

  const restoreOutsideElements = useCallback(() => {
    restoreOutsideElementsRef.current?.();
    restoreOutsideElementsRef.current = null;
    isInsideAllowedNestedModalRef.current = () => false;
    isPreExistingBackgroundElementRef.current = () => false;
  }, []);

  const restoreFocusToTrigger = useCallback(() => {
    if (!isWeb) {
      return;
    }

    const previouslyFocusedElement = previouslyFocusedElementRef.current;
    previouslyFocusedElementRef.current = null;

    if (previouslyFocusedElement && document.contains(previouslyFocusedElement)) {
      previouslyFocusedElement.focus();
    }
  }, []);

  useEffect(() => {
    if (!isWeb || !active) {
      return;
    }

    const modalElement = modalElementRef.current;

    if (!modalElement) {
      return;
    }

    isClosingRef.current = false;
    previouslyFocusedElementRef.current = getActiveHtmlElement();
    restoreOutsideElements();

    const {
      cleanup,
      isInsideAllowedNestedModal,
      isPreExistingBackgroundElement,
    } = hideElementsOutsideModal(modalElement);
    restoreOutsideElementsRef.current = cleanup;
    isInsideAllowedNestedModalRef.current = isInsideAllowedNestedModal;
    isPreExistingBackgroundElementRef.current = isPreExistingBackgroundElement;

    const focusTimeout = setTimeout(() => {
      focusFirstModalElement(modalElement);
    }, 0);

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || isClosingRef.current) {
        return;
      }

      const focusableElements = getModalFocusableElements(modalElement);

      if (!focusableElements.length) {
        event.preventDefault();
        modalElement.focus();
        return;
      }

      const firstFocusableElement = focusableElements[0];
      const lastFocusableElement = focusableElements[focusableElements.length - 1];
      const activeElement = getActiveHtmlElement();
      const isActiveElementOutsideModal = !activeElement || !modalElement.contains(activeElement);

      if (
        isActiveElementOutsideModal &&
        activeElement &&
        (isInsideAllowedNestedModalRef.current(activeElement) ||
          isInsideLiveNestedModal(
            activeElement,
            modalElement,
            isPreExistingBackgroundElementRef.current
          ))
      ) {
        // Focus is inside a nested modal (see handleFocusIn below) — that modal owns Tab
        // handling for its own contents now, so redirecting Tab back into the outer modal would
        // trap keyboard users inside it.
        return;
      }

      if (isActiveElementOutsideModal) {
        event.preventDefault();
        firstFocusableElement.focus();
        return;
      }

      if (event.shiftKey && activeElement === firstFocusableElement) {
        event.preventDefault();
        lastFocusableElement.focus();
        return;
      }

      if (!event.shiftKey && activeElement === lastFocusableElement) {
        event.preventDefault();
        firstFocusableElement.focus();
      }
    };

    const handleFocusIn = (event: FocusEvent) => {
      if (isClosingRef.current) {
        return;
      }

      const target = event.target;

      if (target instanceof HTMLElement && modalElement.contains(target)) {
        return;
      }

      // Focus moving into a nested modal (e.g. the language selector opened from within this
      // trapped drawer) means that modal legitimately owns focus now, so stealing it back would
      // fight that modal's own focus containment in an infinite loop. isInsideAllowedNestedModalRef
      // is the async check (see hideElementsOutsideModal); isInsideLiveNestedModal is the
      // synchronous fallback that can't lose a timing race against it (see its own comment).
      if (
        target instanceof HTMLElement &&
        (isInsideAllowedNestedModalRef.current(target) ||
          isInsideLiveNestedModal(target, modalElement, isPreExistingBackgroundElementRef.current))
      ) {
        return;
      }

      focusFirstModalElement(modalElement);
    };

    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('focusin', handleFocusIn, true);

    return () => {
      clearTimeout(focusTimeout);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('focusin', handleFocusIn, true);
      restoreOutsideElements();
      restoreFocusToTrigger();
    };
  }, [active, isClosingRef, restoreFocusToTrigger, restoreOutsideElements]);

  const getModalA11yProps = useCallback(
    ({ nativeRole }: GetModalA11yPropsOptions) => ({
      accessibilityLabel: label,
      // 'dialog' is a valid web ARIA role passed through as-is by react-native-web, but isn't
      // part of RN's AccessibilityRole type (which only covers native roles), hence the cast.
      accessibilityRole: (isWeb ? 'dialog' : nativeRole) as AccessibilityRole,
      accessibilityViewIsModal: true,
      'aria-modal': isWeb ? true : undefined,
      tabIndex: -1 as const,
    }),
    [label]
  );

  const getBackdropA11yProps = useCallback(
    () => ({
      accessible: false,
      accessibilityElementsHidden: true,
      'aria-hidden': true,
      // Marks the backdrop so hideElementsOutsideModal skips it (it's a legitimate sibling of
      // the modal, not part of the page content the trap is meant to hide).
      dataSet: { modalFocusTrapBackdrop: 'true' },
      focusable: false,
      importantForAccessibility: 'no-hide-descendants' as const,
    }),
    []
  );

  return {
    modalRef,
    restoreOutsideElements,
    restoreFocusToTrigger,
    getModalA11yProps,
    getBackdropA11yProps,
  };
};
